/**
 * §7.8 reconciliation, on demand.
 *
 * The API answers **503** when the balance cache disagrees with a replay of the
 * postings, because that endpoint is meant to be a monitoring target and a 200
 * with `{ok: false}` in the body would graph as healthy. This screen has the
 * same duty in the other direction: a failing reconciliation must not look like
 * a page that loaded fine.
 *
 * There is nothing to click. Drift is not something an operator fixes from a
 * console — `ledger_entries` is append-only and the cache is derived, so the
 * repair is a code path that does not exist and should not be invented in a
 * hurry. What this screen does is tell someone, precisely, so they can go and
 * look.
 */

import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client.ts';
import { at, etb } from '../format.ts';
import { Banner, ErrorBanner, Id, Row, Spinner } from '../components/ui.tsx';
import type { ReconcileReport } from '../api/types.ts';

export function Reconcile() {
  const [report, setReport] = useState<ReconcileReport | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const run = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      setReport(await api.reconcile());
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    void run();
  }, [run]);

  return (
    <section>
      <header className="pagehead">
        <h1>Reconciliation</h1>
        <button type="button" className="button" disabled={busy} onClick={() => void run()}>
          {busy ? 'Running…' : 'Run again'}
        </button>
      </header>

      <ErrorBanner error={error} />
      {report === null && !error ? <Spinner label="Replaying the ledger…" /> : null}

      {report ? (
        <>
          {report.ok ? (
            <Banner kind="ok">
              The balance cache matches a full replay of the postings. Checked{' '}
              {report.accountsChecked} accounts and {report.transactionsChecked} transactions.
            </Banner>
          ) : (
            <Banner kind="error">
              <strong>The ledger does not reconcile.</strong> The cached balances disagree with a
              replay of the postings, which means the platform is currently reporting an amount of
              money that is not the amount the entries add up to. Escalate — do not approve
              withdrawals until this is understood.
            </Banner>
          )}

          <div className="detail__facts">
            <Row label="Checked at">{at(report.checkedAt)}</Row>
            <Row label="Accounts">{report.accountsChecked}</Row>
            <Row label="Transactions">{report.transactionsChecked}</Row>
            <Row label="Total assets">{etb(report.totalAssets)}</Row>
            <Row label="Total liabilities">{etb(report.totalLiabilities)}</Row>
          </div>

          {report.drift.length > 0 ? (
            <div className="card">
              <h2>Accounts that drifted</h2>
              <table className="table" role="table">
                <thead role="rowgroup">
                  <tr role="row">
                    <th scope="col" role="columnheader">Account</th>
                    <th scope="col" role="columnheader">Cached</th>
                    <th scope="col" role="columnheader">Replayed</th>
                    <th scope="col" role="columnheader">Difference</th>
                  </tr>
                </thead>
                <tbody role="rowgroup">
                  {report.drift.map((d) => (
                    <tr role="row" key={d.accountKey}>
                      <td role="cell" data-label="Account">
                        <code>{d.accountKey}</code>
                      </td>
                      <td role="cell" data-label="Cached">{etb(d.cached)}</td>
                      <td role="cell" data-label="Replayed">{etb(d.replayed)}</td>
                      <td role="cell" data-label="Difference" className="flag">{etb(d.delta)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}

          {report.unbalanced.length > 0 ? (
            <div className="card">
              <h2>Transactions that do not balance</h2>
              {/*
                This list should be impossible: a deferred trigger rejects any
                transaction whose debits and credits do not sum to zero, so a
                row here means the constraint was bypassed or disabled. Worth
                saying on screen, because "impossible" findings get dismissed as
                display bugs by the person who happens to be on shift.
              */}
              <Banner kind="error">
                A database trigger is supposed to make this impossible. Entries here mean the
                constraint was disabled or bypassed.
              </Banner>
              <table className="table" role="table">
                <thead role="rowgroup">
                  <tr role="row">
                    <th scope="col" role="columnheader">Transaction</th>
                    <th scope="col" role="columnheader">Debits</th>
                    <th scope="col" role="columnheader">Credits</th>
                  </tr>
                </thead>
                <tbody role="rowgroup">
                  {report.unbalanced.map((u) => (
                    <tr role="row" key={u.transactionId}>
                      <td role="cell" data-label="Transaction">
                        <Id value={u.transactionId} />
                      </td>
                      <td role="cell" data-label="Debits">{etb(u.totalDebit)}</td>
                      <td role="cell" data-label="Credits">{etb(u.totalCredit)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
